import React, { useMemo } from 'react';
import { ThirdwebProvider } from 'thirdweb/react';
import { 
  inAppWallet, 
  createWallet
} from 'thirdweb/wallets';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ACTIVE_CHAIN, NS_CHAIN, PONKE_CHAIN, RIPNDIP_CHAIN } from '../config/chain';

interface ThirdwebProviderWrapperProps {
  children: React.ReactNode;
}

// Function to get chain based on current pathname
function getChainFromPath(pathname: string) {
  if (pathname.includes('ripndip')) {
    return RIPNDIP_CHAIN;
  }
  if (pathname.includes('ponke') || pathname.includes('ripndip')) {
    return PONKE_CHAIN;
  }
  if (pathname.includes('nobodysausage')) {
    return NS_CHAIN;
  }
  // Default to Avalanche for chimpers or other routes
  return ACTIVE_CHAIN;
}

// Function to create wallets with a specific chain
function createWalletsWithChain(chain: typeof ACTIVE_CHAIN | typeof PONKE_CHAIN | typeof RIPNDIP_CHAIN) {
  const inAppOnlyWallets = [
    inAppWallet({
      auth: {
        options: ["email", "google", "apple"],
      },
      smartAccount: {
        chain: chain,
        sponsorGas: true,
      },
    }),
  ];

  const metamaskWallet = createWallet("io.metamask");

  const wallets = [
    ...inAppOnlyWallets,
    metamaskWallet,
    // walletConnect(),
  ];

  return { inAppOnlyWallets, wallets };
}

function createNobodySausageWallets(
  chain: typeof ACTIVE_CHAIN | typeof PONKE_CHAIN | typeof RIPNDIP_CHAIN | typeof NS_CHAIN,
) {
  const inAppOnlyWallets = [
    inAppWallet({
      auth: {
        options: ["email", "google", "apple"],
      },
      smartAccount: {
        chain: chain,
        sponsorGas: true,
      },
    }),
  ];
  const configuredWallets: any[] = [
    ...inAppOnlyWallets,
    // createWallet("io.metamask"),
  ];

  return {
    connectMode: "native-glyph" as const,
    inAppOnlyWallets,
    wallets: configuredWallets,
  };
}

// Default wallets export for backwards compatibility (uses ACTIVE_CHAIN)
const defaultWallets = createWalletsWithChain(ACTIVE_CHAIN);
export const inAppOnlyWallets = defaultWallets.inAppOnlyWallets;
export const metamaskWallet = createWallet("io.metamask");
export const wallets = defaultWallets.wallets;

export function ThirdwebProviderWrapper({ children }: ThirdwebProviderWrapperProps) {
  const queryClient = React.useMemo(() => new QueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThirdwebProvider>
        {children}
      </ThirdwebProvider>
    </QueryClientProvider>
  );
}

// Export function to get wallets with chain based on pathname (for use in components)
export function useRouteChain() {
  const location = useLocation();
  return useMemo(() => getChainFromPath(location.pathname), [location.pathname]);
}

// Export function to get wallets with a specific chain
export function getWalletsWithChain(
  chain: typeof ACTIVE_CHAIN | typeof PONKE_CHAIN | typeof RIPNDIP_CHAIN | typeof NS_CHAIN,
  pathname?: string,
) {
  if (pathname?.includes('nobodysausage')) {
    return createNobodySausageWallets(chain);
  }

  return {
    connectMode: "default" as const,
    ...createWalletsWithChain(chain),
  };
}

export function useRouteWalletConfig() {
  const location = useLocation();
  const routeChain = useRouteChain();

  return useMemo(
    () => getWalletsWithChain(routeChain, location.pathname),
    [location.pathname, routeChain],
  );
}
